import {Injectable} from '@angular/core';
import {map, switchMap} from "rxjs/operators";
import {forkJoin, Observable, of} from "rxjs";
import {CustomEventsService} from "../../../services/custom-events.service";
import {ConstantProperty} from "../../../_models/constant-property";
import {ComponentType} from "../../../_models/component-type";
import {EventType} from "../../../_models/event-type";
import {ListResponse} from "../../../services/api/response-types";
import {
    COMPONENT_EVENTS_CONFIG, ISelectedColsType
} from "../../../_typing/config/component-events-table";
import {CustomEventsConfig} from "../../../forms/custom-events-form/custom-events-form.component";
import {PivotTileConfig} from "../../../_typing/config/pivot-tile";
import {KeyMap, ModelID} from "../../../_typing/generic-types";
import {EventConfigColumn} from "../../../_typing/config/config-column";
import {FieldMap} from "../../../_typing/query-builder";
import {
    getColIds,
    getComponentTypeIds,
    getEventTypeIds,
    getPropertyIds
} from "../../../tables/custom-events-table/utils";
import {fieldFromConstantProperty} from "../query-field.utils";

@Injectable()
export class FieldOptionService {
    config: COMPONENT_EVENTS_CONFIG | CustomEventsConfig | PivotTileConfig;
    allColumns: boolean = true;

    selectedColumns: ISelectedColsType;
    selectedColsDict: KeyMap<EventConfigColumn>;
    fields: FieldMap;

    constantProperties: ConstantProperty[];
    componentTypes: ComponentType[];
    eventTypes: EventType[];

    constructor(public customEventsService: CustomEventsService) {
    }

    getDefaultColumn(name, type, value) {
        if (this.allColumns || this.selectedColsDict?.[value]) {
            return {[value]: {name, type, value}};
        }
        return {};
    }

    getComponentTypeFields(component_type_id: ModelID, defaultFields: FieldMap, columns: EventConfigColumn[] = [],
                           additionalPropertyIds: ModelID[] = []): Observable<FieldMap> {
        const etIds = getEventTypeIds(columns);
        const propertyIds = Array.from(new Set([
            ...getPropertyIds(columns),
            ...additionalPropertyIds
        ]));

        const $componentTypes = this.customEventsService.getComponentTypeComponentTypes(component_type_id)
            .pipe(map(res => {
                this.componentTypes = res.data;
                return this.fieldsFromComponentType(this.componentTypes);
            }));

        const $eventTypes = this.customEventsService.getComponentTypeEventTypes([component_type_id], etIds)
            .pipe(map(res => {
                this.eventTypes = res.data;
                return this.fieldsFromEventType(this.eventTypes);
            }));

        const $properties = propertyIds.length ?
            this.customEventsService.getComponentTypeProperties([component_type_id], propertyIds)
                .pipe(switchMap(res => {
                    this.constantProperties = res.data;
                    return this.fieldsFromConstantProperty(this.constantProperties);
                })) :
            of({});

        return forkJoin([$properties, $componentTypes, $eventTypes]).pipe(
            map(([propertiesField, componentTypesField, eventTypesField]) => {
                return {...defaultFields, ...propertiesField, ...componentTypesField, ...eventTypesField};
            })
        );
    }

    getEventTypeFields(event_type_id: ModelID, defaultFields: FieldMap, columns: EventConfigColumn[] = [],
                       additionalPropertyIds: ModelID[] = []): Observable<FieldMap> {
        const ctIds: ModelID[] = getComponentTypeIds(columns);
        const propertyIds = Array.from(new Set([
            ...getPropertyIds(columns),
            ...additionalPropertyIds
        ]));

        const $componentTypes = this.customEventsService.getEventTypeComponentTypes([event_type_id], ctIds)
            .pipe(map(res => {
                this.componentTypes = res.data;
                return this.fieldsFromComponentType(this.componentTypes);
            }));

        const $properties = propertyIds.length ?
            this.customEventsService.getConstantPropertiesByEventTypeIds([event_type_id], propertyIds)
                .pipe(switchMap((res: ListResponse<ConstantProperty>) => {
                    this.constantProperties = res.data;
                    return this.fieldsFromConstantProperty(this.constantProperties);
                })) :
            of({});

        return forkJoin([$properties, $componentTypes]).pipe(
            map(([propertiesField, componentTypesField]) => {
                return {...defaultFields, ...propertiesField, ...componentTypesField};
            })
        );
    }

    fieldsFromConstantProperty(constantProperties: ConstantProperty[]): Observable<FieldMap> {
        const fields: FieldMap = {};

        constantProperties.forEach(cp => {
            const field = fieldFromConstantProperty(
                cp,
                this.selectedColsDict?.[cp.id]?.title
            );
            if (field) {
                fields[`[constant_property@${cp.id}]`] = field;
            }
        });

        return of(fields);
    }

    fieldsFromComponentType(componentTypes: ComponentType[]): FieldMap {
        return componentTypes.reduce((fields, ct) => {
            fields[`[component_type@${ct.id}]`] = {
                name: ct.attributes.name,
                type: 'boolean'
            };
            return fields;
        }, {});
    }

    fieldsFromEventType(eventTypes: EventType[]): FieldMap {
        return eventTypes.reduce((fields, et) => {
            fields[`[event_type@${et.id}]`] = {
                name: et.attributes.name,
                type: 'boolean'
            };
            return fields;
        }, {});
    }
}
